import { useActionConfirmation } from '../../components/ActionFeedbackProvider';
// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash, Plus, ChevronLeft, ChevronRight, Search, FileDown, Pencil } from 'lucide-react';
import { FaWarehouse } from 'react-icons/fa';
// Animations
import { motion } from 'framer-motion';
// Router
import { useNavigate } from 'react-router-dom';
// Theme hook
import useTheme from '../../hooks/useTheme';
// API client
import api from '../../lib/api';
// Modal components
import AddExitModal from '../../components/modals/AddExitModal';
import EditExitModal from '../../components/modals/EditExitModal';
// i18n
import { useTranslation } from 'react-i18next';

const ExitSTotems = () => {
  const [exits, setExits] = useState([]);
  const [filteredExits, setFilteredExits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  // NEW
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExit, setSelectedExit] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const { theme } = useTheme();

  const locationKey = 'totems';

  const baseBtn =
    'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  const actionBtn =
    'inline-flex items-center justify-center p-2.5 rounded-md shadow transition duration-200 text-sm ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  const getId = (o) => o?.id || o?._id;

  const formatDate = (d) => d.toISOString().split('T')[0];

  const getDayName = (d) =>
    d.toLocaleDateString(undefined, { weekday: 'long' });

  const changeDate = (direction) => {
    const nd = new Date(selectedDate);
    nd.setDate(nd.getDate() + direction);
    setSelectedDate(nd);
  };

  const filterExits = (list, date, term) => {
    const filtered = list.filter((exit) => {
      const d = new Date(exit.exitedAt);

      const sameDay =
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate();

      const searchMatch =
        (exit.productName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.brandName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.supplierName || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.notes || '').toLowerCase().includes(term.toLowerCase()) ||
        (exit.clientName || '').toLowerCase().includes(term.toLowerCase());

      return sameDay && searchMatch;
    });

    setFilteredExits(filtered);
  };

  const loadExits = async () => {
    try {
      setLoading(true);
      // ✅ FIXED ENDPOINT
      const { data } = await api.get(`/inventory/exits?location=${locationKey}`);
      const arr = Array.isArray(data) ? data.slice().reverse() : [];
      setExits(arr);
      filterExits(arr, selectedDate, searchTerm);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadExits'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExits();
  }, []);

  useEffect(() => {
    filterExits(exits, selectedDate, searchTerm);
  }, [exits, selectedDate, searchTerm]);

  const handleDelete = async (id) => {
    if (!await confirmAction(t('confirmDeleteExit'))) return;
    try {
      // ✅ FIXED ENDPOINT
      await api.delete(`/inventory/exits/${id}?location=${locationKey}`);
      await loadExits();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteExit'));
    }
  };

  const handleExport = async (id) => {
    try {
      // ✅ FIXED ENDPOINT
      const res = await api.get(`/inventory/exits/${id}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exit_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      alert(t('errorExport'));
    }
  };

  // NEW edit (safe mode)
  const handleEdit = (exit) => {
    setSelectedExit(exit);
    setShowEditModal(true);
  };

  return (
    <motion.div
      className="px-1 py-4 sm:px-3 sm:py-6 lg:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Date selector */}
      <div className="flex flex-col items-center mb-6 select-none">

        <div className="flex items-center gap-2">

          <button
            onClick={() => changeDate(-1)}
            className="p-2 bg-black text-white rounded-md dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronLeft size={18} />
          </button>

          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 px-3 py-1 rounded-md text-sm dark:bg-gray-800 dark:text-white"
          />

          <button
            onClick={() => changeDate(1)}
            className="p-2 bg-black text-white rounded-md dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronRight size={18} />
          </button>

        </div>

        <h2 className="mt-2 text-xl font-bold dark:text-white capitalize">
          {getDayName(selectedDate)}
        </h2>

      </div>

      <h1 className="text-2xl font-bold text-center mb-4 dark:text-white">
        {t('exitsTotems')}
      </h1>

      {/* Search */}
      <div className="max-w-3xl mx-auto mb-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
        />
      </div>

      <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6">

        <div className="flex justify-between mb-5">
          <div />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowModal(true)} className={baseBtn}>
              <Plus size={16} /> {t('newExit')}
            </button>
            <button
              onClick={() => navigate('/products/inventory/totems')}
              className={baseBtn}
            >
              <FaWarehouse /> {t('inventory')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-600/90 text-white px-4 py-2 rounded-lg mb-3">{error}</div>
        )}

        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredExits.length ? (
          <div className="space-y-2">
            {filteredExits.map((exit) => (
              <div
                key={getId(exit)}
                className="border border-gray-300 dark:border-gray-700 p-3 rounded-md flex justify-between items-center dark:bg-gray-700"
              >
                {/* INFO */}
                <div className="flex flex-col text-sm dark:text-gray-200 w-full leading-tight">

                  <p className="font-semibold text-base dark:text-white truncate">
                    {exit.productName}
                  </p>

                  <p><span className="font-medium">{t('brand')}:</span> {exit.brandName}</p>
                  <p><span className="font-medium">{t('supplier')}:</span> {exit.supplierName}</p>

                  <p>
                    <span className="font-medium">{t('total')}:</span>{' '}
                    {(exit.totalWeight ?? 0).toFixed(2)} KG •{' '}
                    <span className="font-medium">{t('qty')}:</span> {exit.quantityExited}
                  </p>

                  <p className="italic text-sm"><span className="font-medium">{t('exitClient')}:</span> {exit.clientName || t('noClient')}</p>
                  {exit.notes && (
                    <p className="italic"><span className="font-medium">{t('notes')}:</span> {exit.notes}</p>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(exit.exitedAt).toLocaleString()}
                  </p>
                  {exit.userId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{t('createdBy')}:</span> {exit.userId}
                    </p>
                  )}

                </div>

                {/* ACTIONS */}
                <div className="flex gap-2 ml-4 shrink-0">

                  <button onClick={() => handleEdit(exit)} className={actionBtn}>
                    <Pencil size={17} />
                  </button>

                  <button onClick={() => handleExport(getId(exit))} className={actionBtn}>
                    <FileDown size={17} />
                  </button>

                  <button onClick={() => handleDelete(getId(exit))} className={actionBtn}>
                    <Trash size={17} />
                  </button>

                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed dark:border-gray-700 p-5 rounded-md text-gray-500 dark:text-gray-300 text-center">
            {t('noExitsForThisDate')}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showModal && (
        <AddExitModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onCreated={loadExits}
          locationKey={locationKey}
        />
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <EditExitModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          record={selectedExit}
          locationKey={locationKey}
          onUpdated={loadExits}
        />
      )}

    </motion.div>
  );
};

export default ExitSTotems;
